/**
 * PricingPage — public-facing standalone pricing page.
 * Route: /pricing
 *
 * Fetches live plan data from GET /api/subscriptions/plans and shows
 * Author ($39/mo), Publisher ($119/mo), and Studio ($449/mo) tier cards
 * with monthly/yearly toggle, BYOK discount nudge, feature table, FAQ,
 * and founding-member promo badge when active.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Check, X, ChevronRight, Crown, Zap, Star,
  BookOpen, Mic, FileText, Users, ArrowRight, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import PublicNavBar from '@/components/landing/PublicNavBar';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// ── Static feature comparison table ─────────────────────────────────────────
const FEATURES = [
  // ── Core AI writing
  { label: 'AI chapter drafting (Claude, GPT, Gemini)', scribe: true,   author: true,   publisher: true,   studio: true },
  { label: 'Bulk-draft entire novels back-to-back',     scribe: false,  author: false,  publisher: true,   studio: true },
  { label: 'Script conversion (Screenplay / Teleplay)', scribe: false,  author: false,  publisher: true,   studio: true },
  { label: 'Cross-book voice-lock',                     scribe: false,  author: false,  publisher: true,   studio: true },
  // ── Signature editorial tools
  { label: 'Character Bible — full cast management',    scribe: true,   author: true,   publisher: true,   studio: true },
  { label: 'Arc Visualizer — character prominence chart',scribe: true,  author: true,   publisher: true,   studio: true },
  { label: 'Voice Coach — prose voice audit',           scribe: true,   author: true,   publisher: true,   studio: true },
  { label: 'Edit Walker — AI developmental edit pass',  scribe: '1/mo', author: '1/mo', publisher: '3/mo', studio: 'Unlimited' },
  { label: 'Continuity Audit — full-novel deep review', scribe: false,  author: false,  publisher: true,   studio: true },
  // ── Planning & publishing
  { label: 'Plot Blueprint + Sales Readiness Score',    scribe: true,   author: true,   publisher: true,   studio: true },
  { label: 'Marketing Asset Generator',                 scribe: true,   author: true,   publisher: true,   studio: true },
  { label: 'ARC Outreach Tracker / CRM',                scribe: true,   author: true,   publisher: true,   studio: true },
  { label: 'ISBN Manager + Adobe PDF Export',           scribe: true,   author: true,   publisher: true,   studio: true },
  { label: 'Publish direct to Bookstore (85% royalties)',scribe: true,  author: true,   publisher: true,   studio: true },
  // ── Add-ons & limits
  { label: 'Audiobook narration',                       scribe: false,  author: false,  publisher: 'Pay per use', studio: 'Pay per use' },
  { label: 'Audiobook production pricing — Standard TTS $14.99 · Fish Audio cloned $34.99 · BYOK platform fee $24.99 · Unlock fee $12.99', scribe: false, author: false, publisher: true, studio: true },
  { label: 'Canva cover design',                        scribe: 'Pay per cover', author: 'Pay per cover', publisher: 'Pay per cover', studio: 'Pay per cover' },
  { label: 'Token top-up packs',                        scribe: false,  author: true,   publisher: true,   studio: true },
  { label: 'AI tokens / month',                         scribe: 'BYOK only', author: '1.5 M', publisher: '4.5 M', studio: '20 M' },
  { label: 'Copyscape plagiarism scans',                scribe: '25/mo',author: '50/mo',publisher: '200/mo',studio: '500/mo' },
  { label: 'Human editorial services',                  scribe: 'Cafeteria add-on', author: 'Cafeteria add-on', publisher: 'Cafeteria add-on', studio: 'Cafeteria add-on' },
  { label: 'Writer coaching',                           scribe: 'Cafeteria add-on', author: 'Cafeteria add-on', publisher: 'Cafeteria add-on', studio: 'Cafeteria add-on' },
  { label: 'Bring your own AI key (BYOK)',              scribe: 'Required', author: true, publisher: true, studio: true },
];

// ── FAQ ───────────────────────────────────────────────────────────────────────
const FAQ = [
  {
    q: 'What is the difference between Author and Publisher?',
    a: 'Author gives you the full AI toolkit (Character Bible, Arc Visualizer, Edit Walker, Voice Coach, Plot Blueprint, Marketing Assets) to write and publish one novel at a time. Publisher adds bulk-drafting, Continuity Audit, script conversion, audiobook narration (pay per production), and cross-book voice-lock — ideal for writers building a catalogue. Studio adds premium AI models, 5-novels-in-parallel drafting, and unlimited Edit Walker passes for career authors.',
  },
  {
    q: 'Can I try before paying?',
    a: "Yes. The Writer's Launch promo gives you 3–5 AI-drafted chapters at zero cost — no credit card required. When you're ready to keep your draft and unlock unlimited chapters, upgrade to Author.",
  },
  {
    q: 'What does BYOK mean and how much does it save?',
    a: 'BYOK (Bring Your Own Key) lets you connect your own OpenAI, Anthropic, or Gemini API key in Settings → Billing. Crown & Rose routes every AI call through your personal key, dropping our COGS to zero — which means Author BYOK is $25/mo instead of $39/mo (36% off).',
  },
  {
    q: 'Is there a yearly discount?',
    a: 'Yes — every paid plan is 20% off when billed annually. Scribe yearly = $134.40/yr ($11.20/mo), Author yearly = $374.40/yr ($31.20/mo), Publisher yearly = $1,142.40/yr ($95.20/mo), Studio yearly = $4,310.40/yr ($359.20/mo). Annual billing is the best value — you get two months free.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Cancel from Settings → My Account → Manage subscription. Your access continues until the end of the current billing period. No cancellation fees.',
  },
  {
    q: 'How do AI tokens work?',
    a: "Every AI action (draft chapter, rewrite, chat) consumes tokens. Author includes 1.5 M tokens/mo — enough to complete a full novel draft each month. Publisher gets 4.5 M for multi-title production; Studio gets 20 M with access to all premium models. If you hit the limit, top up with a token pack from the Wallet.",
  },
  {
    q: 'How does audiobook narration work?',
    a: 'Publisher and Studio plans can generate a full AI-narrated audiobook for any novel. Pricing tiers:\n\n• Free sample: the first chapter is narrated free so you can audition the voice.\n• Standard TTS ($14.99): generate the full audiobook upfront using OpenAI TTS — best value for full-length novels.\n• Fish Audio cloned voice ($34.99 / novel): a natural, cloned-voice narration powered by Fish Audio — distinctive and personal.\n• BYOK TTS platform fee ($24.99 / novel): bring your own OpenAI or ElevenLabs key and pay only a $24.99 platform fee per production.\n• Unlock fee ($12.99 for sub-Studio tiers): unlock chapter-by-chapter generation on Author-tier books.\n\nYou receive MP3 files you can sell or distribute anywhere, including the Crown & Rose bookstore.',
  },
];

// ── Tier accent colours — adjusted for light background ───────────────────────
const TIER_ACCENT = {
  scribe:    { ring: 'ring-emerald-500/60', badge: 'bg-emerald-100 text-emerald-700', btn: 'bg-emerald-600 hover:bg-emerald-500 text-white' },
  author:    { ring: 'ring-amber-500/60',   badge: 'bg-amber-100 text-amber-800',    btn: 'bg-amber-500 hover:bg-amber-400 text-black' },
  publisher: { ring: 'ring-violet-500/60',  badge: 'bg-violet-100 text-violet-700',  btn: 'bg-violet-600 hover:bg-violet-500 text-white' },
  studio:    { ring: 'ring-fuchsia-500/60', badge: 'bg-fuchsia-100 text-fuchsia-700',btn: 'bg-fuchsia-600 hover:bg-fuchsia-500 text-white' },
};

function FeatureVal({ val }) {
  if (val === true)  return <Check className="h-4 w-4 text-emerald-600 mx-auto" />;
  if (val === false) return <X     className="h-4 w-4 text-stone-300 mx-auto" />;
  return <span className="text-[11.5px] text-stone-700 font-mono-stat">{val}</span>;
}

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-stone-200" data-testid={`faq-item-${index}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between py-4 text-left text-[14px] font-medium text-neutral-100 hover:text-amber-300 transition-colors"
        data-testid={`pricing-faq-${q.slice(0, 20).toLowerCase().replace(/\s+/g, '-')}`}
      >
        {q}
        <ChevronRight className={cn('h-4 w-4 flex-none text-neutral-500 transition-transform', open && 'rotate-90')} />
      </button>
      {open && (
        <p className="pb-4 text-[13.5px] leading-relaxed text-neutral-400">{a}</p>
      )}
    </div>
  );
}

// ── BYOK Savings Calculator ───────────────────────────────────────────────────
const BYOK_DATA = {
  author:    { name: 'Author',    standard: 39,  byok: 25,  color: '#f59e0b' },
  publisher: { name: 'Publisher', standard: 119, byok: 79,  color: '#8b5cf6' },
  studio:    { name: 'Studio',    standard: 449, byok: 299, color: '#d946ef' },
};

function tierFromChapters(n) {
  if (n <= 8)  return 'author';
  if (n <= 20) return 'publisher';
  return 'studio';
}
const PACE_LABELS = [
  [4,  'Just getting started'],
  [8,  'Occasional writer'],
  [14, 'Active author'],
  [20, 'Serious novelist'],
  [28, 'Prolific creator'],
  [30, 'Full-time author'],
];
function paceLabel(n) {
  return (PACE_LABELS.find(([max]) => n <= max) || PACE_LABELS.at(-1))[1];
}

function ByokCalculator() {
  const [chapters, setChapters] = useState(10);
  const [manualTier, setManualTier] = useState(null);

  const autoTier   = tierFromChapters(chapters);
  const activeTier = manualTier ?? autoTier;
  const plan       = BYOK_DATA[activeTier];

  const annualStd    = plan.standard * 12;
  const annualByok   = plan.byok * 12;
  const annualSaving = annualStd - annualByok;
  const savingPct    = Math.round((annualSaving / annualStd) * 100);
  const equivMonths  = (annualSaving / plan.byok).toFixed(1);

  const fillPct = ((chapters - 1) / 29) * 100;

  return (
    <section className="mx-auto max-w-5xl px-4 pb-6" data-testid="byok-calculator">
      <div className="relative overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
        {/* Warm ambient glow */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-28 left-1/2 h-56 w-96 -translate-x-1/2 rounded-full opacity-[0.18] blur-3xl"
          style={{ background: 'hsl(28 90% 45%)' }}
        />
        <div className="relative z-10 px-6 py-8 sm:px-10">
          {/* Section header */}
          <div className="mb-7">
            <span
              className="mb-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-widest"
              style={{ borderColor: 'hsl(28 90% 45% / 0.35)', color: 'hsl(28 90% 55%)', background: 'hsl(28 90% 45% / 0.07)' }}
            >
              BYOK Savings Calculator
            </span>
            <h2 className="text-lg font-bold text-stone-900">How much would you save with your own AI key?</h2>
            <p className="mt-1 text-sm text-neutral-400">Drag to your writing pace — we&apos;ll show you the exact annual saving.</p>
          </div>

          <div className="grid gap-8 lg:grid-cols-2">
            {/* ─ Left: slider + tier pills ─ */}
            <div className="space-y-7">
              {/* Slider */}
              <div>
                <div className="mb-3 flex items-baseline justify-between">
                  <span className="text-[11.5px] font-semibold uppercase tracking-wider text-neutral-400">Chapters / month</span>
                  <span className="text-[28px] font-black leading-none tabular-nums" style={{ color: plan.color }}>{chapters}</span>
                </div>
                {/* Native range with dynamic fill via gradient */}
                <input
                  type="range"
                  min={1} max={30} step={1}
                  value={chapters}
                  onChange={(e) => { setChapters(Number(e.target.value)); setManualTier(null); }}
                  data-testid="byok-chapters-slider"
                  aria-label="Chapters per month"
                  className="w-full cursor-pointer appearance-none rounded-full focus:outline-none"
                  style={{
                    height: '6px',
                    background: `linear-gradient(to right, ${plan.color} 0%, ${plan.color} ${fillPct}%, #262626 ${fillPct}%, #262626 100%)`,
                    // thumb colour via accent-color (Chromium 93+, Firefox 81+)
                    accentColor: plan.color,
                  }}
                />
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[10px] text-neutral-700">1</span>
                  <span
                    className="rounded-full border px-2 py-0.5 text-[11px] font-semibold"
                    style={{ borderColor: `${plan.color}40`, color: plan.color, background: `${plan.color}12` }}
                  >
                    {paceLabel(chapters)}
                  </span>
                  <span className="text-[10px] text-neutral-700">30</span>
                </div>
              </div>

              {/* Tier pills */}
              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-600">
                  {manualTier ? 'Plan (manually chosen)' : `Recommended plan for ${chapters} ch/mo`}
                </div>
                <div className="flex gap-2">
                  {Object.entries(BYOK_DATA).map(([key, p]) => {
                    const active = activeTier === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        data-testid={`byok-tier-pill-${key}`}
                        onClick={() => setManualTier(manualTier === key ? null : key)}
                        aria-pressed={active}
                        className="flex-1 rounded-lg border px-2 py-2 text-[12px] font-semibold transition-all hover:opacity-90"
                        style={{
                          background:   active ? `${p.color}18` : 'transparent',
                          borderColor:  active ? p.color : '#3a3a3a',
                          color:        active ? p.color : '#6b7280',
                        }}
                      >
                        {p.name}
                        {!manualTier && autoTier === key && (
                          <span className="ml-1 text-[9px] opacity-60">★ auto</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Explainer note */}
              <p className="text-[11.5px] leading-relaxed text-neutral-600">
                BYOK routes every AI call through your own OpenAI, Anthropic, or Google key — platform features stay identical. Swap back at any time in Settings.
              </p>
            </div>

            {/* ─ Right: savings display ─ */}
            <div
              className="flex flex-col rounded-2xl border p-6"
              style={{ borderColor: `${plan.color}30`, background: `${plan.color}07` }}
            >
              {/* Price cards */}
              <div className="mb-6 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-stone-200 bg-stone-50 px-4 py-3">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">Standard</div>
                  <div className="text-[22px] font-bold text-neutral-200">
                    ${plan.standard}<span className="text-[11px] font-normal text-neutral-500">/mo</span>
                  </div>
                  <div className="text-[11px] text-neutral-600 mt-0.5">${annualStd.toLocaleString()} / year</div>
                </div>
                <div
                  className="rounded-xl border px-4 py-3"
                  style={{ borderColor: `${plan.color}50`, background: `${plan.color}10` }}
                >
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: plan.color }}>With BYOK</div>
                  <div className="text-[22px] font-bold" style={{ color: plan.color }}>
                    ${plan.byok}<span className="text-[11px] font-normal text-neutral-400">/mo</span>
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">${annualByok.toLocaleString()} / year</div>
                </div>
              </div>

              {/* Savings number — hero moment */}
              <div className="mb-6 flex-1 flex flex-col items-center justify-center text-center py-2">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-neutral-500 mb-1">Annual saving</div>
                <div
                  className="text-[52px] font-black leading-none tabular-nums transition-all duration-300"
                  style={{ color: plan.color }}
                  data-testid="byok-annual-saving"
                >
                  ${annualSaving.toLocaleString()}
                </div>
                <div className="mt-2 text-[12.5px] text-neutral-400">
                  {savingPct}% off &nbsp;·&nbsp; ~{equivMonths} months free
                </div>
              </div>

              {/* CTA */}
              <a
                href="/login?intent=register&byok=1"
                data-testid="byok-calculator-cta"
                className="flex w-full items-center justify-center gap-2 rounded-xl py-3 text-[13px] font-bold text-white transition-all hover:opacity-90 active:scale-[0.98]"
                style={{ background: plan.color }}
              >
                Start {plan.name} with BYOK <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function PricingPage() {
  const navigate = useNavigate();
  const [cadence, setCadence]   = useState('monthly');
  const [plans, setPlans]       = useState([]);
  const [founding, setFounding] = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/subscriptions/plans`).then(r => r.json()),
      fetch(`${API}/subscriptions/founding-promo`).then(r => r.json()).catch(() => null),
    ]).then(([plansData, foundingData]) => {
      const visible = (plansData.plans || []).filter(p =>
        ['scribe', 'author', 'publisher', 'studio'].includes(p.id)
      );
      setPlans(visible);
      if (foundingData?.active) setFounding(foundingData);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const getPrice = (plan) => {
    const base = cadence === 'monthly'
      ? plan.monthly?.price_usd ?? 0
      : plan.yearly?.price_usd ?? 0;
    return base;
  };

  const displayPrice = (plan) => {
    const p = getPrice(plan);
    if (cadence === 'yearly') return `$${(p / 12).toFixed(0)}`;
    return `$${p}`;
  };

  // JSON-LD for pricing page
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "name": "Crown & Rose Pricing — Author, Publisher, Studio",
    "mainEntity": FAQ.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <>
      <Helmet>
        <title>Pricing — Scribe, Author, Publisher & Studio Plans | Crown & Rose</title>
        <meta name="description" content="Simple, honest pricing for indie authors. Scribe $14/mo — BYOK writers. Author $39/mo — full AI toolkit. Publisher $119/mo — bulk drafting + scripts. Studio $449/mo — every tool, every model. Cancel anytime." />

        <meta name="keywords" content="crown and rose pricing, AI novel writing subscription, scribe plan $14, author plan $39, publisher plan $119, studio plan $449, indie author tools pricing, byok discount, annual plan savings" />
        <meta property="og:type"        content="website" />
        <meta property="og:title"       content="Pricing — Crown & Rose AI Novel Writing Studio" />
        <meta property="og:description" content="Four tiers for indie authors. Scribe $14/mo (BYOK), Author $39/mo (full AI toolkit), Publisher $119/mo, Studio $449/mo (all capabilities, 20M tokens). No per-generation fees. Cancel anytime." />
        <meta property="og:image"       content="https://static.prod-images.emergentagent.com/jobs/67d45115-9fac-4a11-82ed-b7a8bff08f0b/images/8d75a73125d9ed24cf3f04b732ce2568aaea3df859a8a9be1b31e43def8fb176.jpeg" />
        <meta property="og:image:width" content="1264" />
        <meta property="og:image:height" content="848" />
        <meta name="twitter:card"       content="summary_large_image" />
        <meta name="twitter:title"      content="Pricing — Crown & Rose AI Novel Writing Studio" />
        <meta name="twitter:description" content="Four tiers: Scribe $14/mo (BYOK) · Author $39/mo · Publisher $119/mo · Studio $449/mo. Full AI toolkit, no per-generation fees." />
        <meta name="twitter:image"      content="https://static.prod-images.emergentagent.com/jobs/67d45115-9fac-4a11-82ed-b7a8bff08f0b/images/8d75a73125d9ed24cf3f04b732ce2568aaea3df859a8a9be1b31e43def8fb176.jpeg" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-stone-50 text-stone-900">
        <PublicNavBar />

        {/* ── Hero ── */}
        <section className="mx-auto max-w-5xl px-4 pt-24 pb-12 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/8 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-amber-400 mb-6" data-testid="pricing-hero-badge">
            <Crown className="h-3 w-3" /> Simple pricing
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-stone-900 leading-[1.08]">
            Write your next novel.<br />
            <span className="text-amber-600">Not your billing spreadsheet.</span>
          </h1>
          <p className="mt-5 text-lg text-stone-500 max-w-2xl mx-auto">
            Four tiers for every stage of your author journey — from finishing your first draft to running a full publishing catalogue.
          </p>

          {/* ── Founding Member Banner ── */}
          {founding && (
            <div className="mt-6 inline-flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-50 px-4 py-2 text-[13px] text-emerald-700" data-testid="pricing-founding-banner">
              <Star className="h-3.5 w-3.5 text-emerald-400 flex-none" />
              <strong>Founding Member offer:</strong> {founding.percent_off}% off your first {founding.months} months on Author — {founding.redemptions_remaining} spots left, ends {new Date(founding.redeem_by).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.
            </div>
          )}

          {/* ── Cadence Toggle ── */}
          <div className="mt-8 inline-flex items-center rounded-lg border border-stone-300 bg-stone-100 p-1 gap-0.5" data-testid="pricing-cadence-toggle">
            <button
              onClick={() => setCadence('monthly')}
              data-testid="pricing-cadence-monthly"
              className={cn('rounded-md px-5 py-2 text-[13px] font-medium transition', cadence === 'monthly' ? 'bg-stone-700 text-white' : 'text-stone-500 hover:text-stone-700')}
            >Monthly</button>
            <button
              onClick={() => setCadence('yearly')}
              data-testid="pricing-cadence-yearly"
              className={cn('rounded-md px-5 py-2 text-[13px] font-medium inline-flex items-center gap-2 transition', cadence === 'yearly' ? 'bg-stone-700 text-white' : 'text-stone-500 hover:text-stone-700')}
            >
              Yearly
              <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">SAVE 20%</span>
            </button>
          </div>
        </section>

        {/* ── Plan Cards ── */}
        <section className="mx-auto max-w-5xl px-4 pb-16">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-20 text-neutral-500">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading plans…
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-4" data-testid="pricing-plan-cards">
              {plans.map((plan) => {
                const accent  = TIER_ACCENT[plan.id] || TIER_ACCENT.author;
                const isBest  = plan.id === 'author';
                const price   = displayPrice(plan);
                const monthly = plan.monthly?.price_usd ?? 0;
                const yearly  = plan.yearly?.price_usd  ?? 0;
                const saving  = monthly > 0 ? Math.round(monthly * 12 - yearly) : 0;
                const byokMonthly = plan.byok_monthly_price_usd;

                return (
                  <div
                    key={plan.id}
                    data-testid={`pricing-plan-${plan.id}`}
                    className={cn(
                      'relative flex flex-col rounded-2xl border bg-white p-7 transition-shadow shadow-sm',
                      isBest ? `${accent.ring} ring-2 border-transparent` : 'border-stone-200 hover:border-stone-400 hover:shadow-md',
                    )}
                  >
                    {isBest && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1 text-[11px] font-bold text-black uppercase tracking-wider" data-testid="pricing-best-badge">
                          <Star className="h-3 w-3" /> Most Popular
                        </span>
                      </div>
                    )}

                    {/* Plan header */}
                    <div>
                      <span className={cn('inline-block rounded-md px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider mb-3', accent.badge)}>
                        {plan.name}
                      </span>
                      <div className="flex items-end gap-1 mb-1">
                        <span className="text-5xl font-bold text-stone-900 leading-none" data-testid={`pricing-plan-price-${plan.id}`}>{price}</span>
                        <span className="mb-1 text-[13px] text-stone-400">/{cadence === 'yearly' ? 'mo, billed yearly' : 'mo'}</span>
                      </div>
                      {cadence === 'yearly' && saving > 0 && (
                        <div className="text-[12px] text-emerald-400 font-medium mb-1" data-testid={`pricing-plan-saving-${plan.id}`}>
                          Save ${saving} per year
                        </div>
                      )}
                      {byokMonthly != null && cadence === 'monthly' && (
                        <div className="text-[11.5px] text-stone-500" data-testid={`pricing-plan-byok-${plan.id}`}>
                          BYOK: <span className="text-amber-600 font-semibold">${byokMonthly}/mo</span> with your own API key
                        </div>
                      )}
                      <p className="mt-3 text-[13px] text-stone-500 leading-relaxed">{plan.tagline}</p>
                    </div>

                    {/* CTA */}
                    <div className="mt-6">
                      <Button
                        onClick={() => navigate('/login?intent=subscribe&plan=' + plan.id)}
                        data-testid={`pricing-plan-cta-${plan.id}`}
                        className={cn('w-full rounded-xl font-semibold text-[13.5px] py-2.5 transition-all', accent.btn)}
                      >
                        {plan.id === 'author' ? 'Start with Author' : `Get ${plan.name}`}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>

                    {/* Feature bullets */}
                    {plan.features && (
                      <ul className="mt-6 space-y-2.5">
                        {plan.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-[13px] text-stone-700">
                            <Check className="h-3.5 w-3.5 text-emerald-500 flex-none mt-0.5" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Token + Copyscape info */}
                    <div className="mt-6 pt-4 border-t border-stone-100 text-[11.5px] text-stone-400 space-y-1">
                      <div className="flex items-center gap-1.5">
                        <Zap className="h-3 w-3 text-amber-500/70" />
                        {(plan.monthly?.tokens ?? 0).toLocaleString()} tokens/mo
                      </div>
                      {plan.copyscape_included > 0 && (
                        <div className="flex items-center gap-1.5">
                          <FileText className="h-3 w-3 text-blue-400/70" />
                          {plan.copyscape_included} Copyscape scans/mo
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Writer's Launch free option */}
          <div className="mt-6 rounded-xl border border-stone-200 bg-stone-100 p-5 text-center" data-testid="pricing-free-cta">
            <p className="text-[13px] text-stone-600">
              Not ready to subscribe?{' '}
              <Link to="/login?intent=trial" className="text-amber-400 hover:text-amber-300 font-medium underline underline-offset-2">
                Start the Writer&apos;s Launch — 3–5 AI-drafted chapters, no card required.
              </Link>
            </p>
          </div>
        </section>

        {/* ── BYOK Savings Calculator ── */}
        <ByokCalculator />

        {/* ── Feature Comparison Table ── */}
        <section className="mx-auto max-w-5xl px-4 pb-20" data-testid="pricing-comparison-table">
          <h2 className="text-2xl font-bold text-stone-900 mb-8 text-center">Full feature comparison</h2>
          <div className="[overflow-x:clip] rounded-xl border border-stone-200">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-stone-200 bg-stone-100 backdrop-blur-sm">
                  <th className="py-3 px-5 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500 w-1/2">Feature</th>
                  <th className="py-3 px-3 text-center text-[11px] font-semibold uppercase tracking-wider text-emerald-600">Scribe<br /><span className="font-normal text-stone-400 normal-case">$14/mo</span></th>
                  <th className="py-3 px-3 text-center text-[11px] font-semibold uppercase tracking-wider text-amber-600">Author<br /><span className="font-normal text-stone-400 normal-case">$39/mo</span></th>
                  <th className="py-3 px-3 text-center text-[11px] font-semibold uppercase tracking-wider text-violet-600">Publisher<br /><span className="font-normal text-stone-400 normal-case">$119/mo</span></th>
                  <th className="py-3 px-3 text-center text-[11px] font-semibold uppercase tracking-wider text-fuchsia-600">Studio<br /><span className="font-normal text-stone-400 normal-case">$449/mo</span></th>
                </tr>
              </thead>
              <tbody>
                {FEATURES.map((f, i) => (
                  <tr
                    key={i}
                    className={cn('border-b border-stone-100', i % 2 === 0 ? 'bg-stone-50/80' : 'bg-white')}
                    data-testid={`pricing-feature-row-${i}`}
                  >
                    <td className="py-3 px-5 text-stone-700">{f.label}</td>
                    <td className="py-3 px-3 text-center"><FeatureVal val={f.scribe} /></td>
                    <td className="py-3 px-3 text-center"><FeatureVal val={f.author} /></td>
                    <td className="py-3 px-3 text-center"><FeatureVal val={f.publisher} /></td>
                    <td className="py-3 px-3 text-center"><FeatureVal val={f.studio} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="mx-auto max-w-3xl px-4 pb-20" data-testid="pricing-faq-section">
          <h2 className="text-2xl font-bold text-stone-900 mb-8 text-center">Frequently asked questions</h2>
          <div>
            {FAQ.map((f, i) => <FaqItem key={i} index={i} q={f.q} a={f.a} />)}
          </div>
        </section>

        {/* ── Token Top-Up Packs ── */}
        <section className="mx-auto max-w-4xl px-4 pb-16" data-testid="pricing-topup-section">
          <h2 className="text-2xl font-bold text-stone-900 mb-2 text-center">Need more tokens?</h2>
          <p className="text-center text-[14px] text-stone-500 mb-8">
            Every plan can top up on demand. Buy a one-time pack — tokens never expire and stack with your monthly allowance.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { name: 'Starter', tokens: '50 K',  price: '$10',  desc: 'Quick top-up for a chapter sprint', id: 'starter' },
              { name: 'Pro',     tokens: '250 K', price: '$40',  desc: 'Several full chapters with revision headroom', id: 'pro', best: true },
              { name: 'Power',   tokens: '1 M',   price: '$120', desc: 'Full novel draft plus rewrites', id: 'power' },
            ].map((pack) => (
              <div
                key={pack.id}
                className={`relative rounded-xl border p-5 text-left ${pack.best ? 'border-amber-500/60 bg-amber-50' : 'border-stone-200 bg-white'}`}
                data-testid={`topup-pack-${pack.id}`}
              >
                {pack.best && (
                  <span className="absolute -top-2.5 left-4 rounded-full bg-amber-500 px-2.5 py-0.5 text-[10px] font-semibold text-black uppercase tracking-wider">Most popular</span>
                )}
                <p className="text-[11px] font-semibold uppercase tracking-widest text-stone-400 mb-1">{pack.name}</p>
                <p className="text-3xl font-bold text-stone-900">{pack.price}</p>
                <p className="text-[13px] text-amber-600 font-medium mt-0.5">{pack.tokens} tokens</p>
                <p className="text-[12px] text-stone-500 mt-2">{pack.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[11.5px] text-stone-400 mt-5">
            Top up from <strong className="text-stone-600">File → Wallet → Buy Tokens</strong> inside the editor.
          </p>
        </section>

        {/* ── Bottom CTA ── */}
        <section className="mx-auto max-w-5xl px-4 pb-24 text-center">
          <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-10">
            <Crown className="h-8 w-8 text-amber-500 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-stone-900 mb-3">Ready to finish your novel?</h2>
            <p className="text-[15px] text-stone-500 mb-7 max-w-xl mx-auto">
              Join thousands of indie authors writing with AI — without giving up their voice.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => navigate('/login?intent=trial')}
                variant="outline"
                className="border-amber-500/60 text-amber-700 hover:bg-amber-50 rounded-xl px-7"
                data-testid="pricing-cta-trial"
              >
                Start free — no card needed
              </Button>
              <Button
                onClick={() => navigate('/login?intent=subscribe&plan=author')}
                className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-xl px-7"
                data-testid="pricing-cta-author"
              >
                Get Author — $39/mo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <p className="mt-4 text-[11.5px] text-stone-400">Cancel anytime · Scribe BYOK from $14/mo · 20% off yearly</p>
          </div>
        </section>

        {/* ── Comparison Page Links ── */}
        <section className="mx-auto max-w-5xl px-4 pb-16 text-center">
          <p className="text-[12.5px] text-stone-400 mb-3">Comparing your options?</p>
          <div className="flex flex-wrap items-center justify-center gap-3 text-[12px]">
            {['scrivener', 'atticus', 'vellum', 'prowritingaid', 'jasper'].map(slug => (
              <Link
                key={slug}
                to={`/compare/crown-and-rose-vs-${slug}`}
                className="text-stone-400 hover:text-amber-600 transition-colors underline underline-offset-2"
                data-testid={`pricing-compare-link-${slug}`}
              >
                Crown &amp; Rose vs {slug.charAt(0).toUpperCase() + slug.slice(1)}
              </Link>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
